<template>
    <jet-dialog-modal ref="modal" :show="show_modal" maxWidth="xl" @close="close">
        <template v-slot:title>
            <div class="flex text-gray-700 text-base font-medium">
                <span>Configure your Webhook</span>
                <!-- <vue-feather type="link-2" class="h-5 w-5 ml-2 self-center"></vue-feather> -->
            </div>
        </template>
        <template v-slot:content>

            <div class="text-gray-600 text-sm font-normal mb-5">
                For more information about webhook setup <a
                    href="https://docs.birdie.so/birdie-docs/birdie-api/webhook-integration-guide" target="_blank"
                    class="text-birdie-blue hover:underline">please refer to our docs.</a>
            </div>

            <div class="text-gray-600 grid grid-cols-6 gap-4">

                <div class="col-span-6 flex flex-col justify-center ">
                    <span class="text-sm font-bold pl-1 flex items-center">Your POST Https Enpoint
                        <InformationCircleIcon class="w-4 h-4 ml-1 text-gray-400 cursor-pointer"
                            v-tippy="{ content: 'Must start with https:// and accept POST requests', appendTo: 'parent', zIndex: 99999, placement: 'right' }">
                        </InformationCircleIcon></span>
                    <div class="relative">
                        <jet-input id="webhook_url" type="text" ref="webhook_url" class="mt-1 mr-6 block w-full pr-12"
                            v-model="webhook.url" :disabled="testing" placeholder="https://acme.com/birdie/webhook"
                            @input="urlChanged">
                        </jet-input>

                        <div :class="[
                            validUrl ? 'opacity-100 cursor-pointer' : 'opacity-0 pointer-events-none',
                            'absolute mt-0.5 right-1 top-1/2 -translate-y-1/2 size-8 p-1 cursor-pointer rounded-md bg-white flex justify-center items-center transition-opacity duration-300 group']"
                            v-tippy="{ content: testButtonTooltip, appendTo: 'parent', zIndex: 99999, placement: 'top' }"
                            @click.prevent.stop="test">
                            <spinner v-if="testing" class="w-4 h-4 ml-1 text-gray-400" />
                            <XMarkIcon v-else-if="test_failed" class="text-red-500 w-full h-full" />
                            <CheckIcon v-else-if="test_succeeded" class="text-green-500 w-full h-full" />
                            <PlayIcon v-else class="text-gray-800 group-hover:text-birdie-blue size-4" />
                        </div>

                    </div>

                </div>

                <div class="col-span-6 flex flex-col justify-center ">
                    <span class="text-sm font-bold pl-1 flex items-center">Signing secret
                        <InformationCircleIcon class="w-4 h-4 ml-1 text-gray-400 cursor-pointer"
                            v-tippy="{ content: 'Your secret key to authenticate the webhook.', appendTo: 'parent', zIndex: 99999, placement: 'right' }">
                        </InformationCircleIcon>
                    </span>
                    <CopyField :copyText="webhook.secret" />
                </div>

            </div>
        </template>
        <template v-slot:footer>
            <div class="flex w-full flex-row justify-between">
                <div class="flex w-full">
                    <jet-button class="w-full place-content-center" :disabled="!formValid" @click.prevent.stop="save">
                        Save
                    </jet-button>
                </div>
            </div>
        </template>
    </jet-dialog-modal>
</template>

<script>

import JetDialogModal from '@/Jetstream/DialogModal'
import JetButton from '@/Jetstream/Button'
import JetInput from '@/Jetstream/Input'
import JetLabel from '@/Jetstream/Label'
import { InformationCircleIcon, PlayIcon, CheckIcon, XMarkIcon } from "@heroicons/vue/24/solid";
import CopyField from '@/Components/UI/CopyField'
import Spinner from '@/Components/UI/Spinner'


export default {

    props: {
        the_webhook: {
            type: Object,
            required: false
        },
        temp_webhook_secret: {
            type: String,
            required: false
        }
    },

    components: {
        JetDialogModal, JetButton, JetInput, JetLabel,
        InformationCircleIcon, CopyField, Spinner,
        PlayIcon, CheckIcon, XMarkIcon
    },

    data() {
        return {
            show_modal: false,
            webhook: this.the_webhook,
            initialUrl: null,
            testing: false,
            testing_timeout: null,
            test_failed: null,
            test_succeeded: null,
            is_webhook_creation: false,
            last_test_succeeded: true
        }
    },

    created: function () {

    },

    emits: ["callback"],

    methods: {

        open: function () {
            this.show_modal = true
            if (!this.webhook) {
                this.is_webhook_creation = true
                this.last_test_succeeded = false
                this.webhook = {
                    url: '',
                    secret: this.temp_webhook_secret
                }
            }
            this.initialUrl = this.webhook.url

            setTimeout(function () {
                this.$refs.webhook_url.focus()
            }.bind(this), 300)
        },
        urlChanged: function (element, value) {
            this.testing = false
            this.test_failed = null
            this.test_succeeded = null
            this.last_test_succeeded = false
        },
        close: function () {
            this.$emit('callback', {
                webhook_url: null,
                webhook_secret: null
            })
            this.show_modal = false
        },
        test() {
            if (!this.validUrl || this.testing) {
                return
            }
            this.testing = true
            this.test_failed = null
            this.test_succeeded = null
            this.testing_timeout = setTimeout(() => {
                this.testing = false
                this.test_failed = 'Timeout'
                this.last_test_succeeded = false
            }, 3000)

            const url = this.$page.props.api_url + '/v1/webhook/test';
            const params = {
                secret: this.webhook.secret,
                url: this.webhook.url
            };

            axios.post(url, params).then(response => {
                // console.log("response", response)
                const status = response.status
                if (status >= 200 && status < 300) {
                    this.test_succeeded = 'OK (' + status + ')'
                    this.last_test_succeeded = true
                } else {
                    this.test_failed = 'Error (' + status + ')'
                    this.last_test_succeeded = false
                }
            }).catch(error => {
                // console.log("error", error)
                this.test_failed = 'Error (' + error.response.status + ')'
                this.last_test_succeeded = false
            }).finally(() => {
                this.testing = false
                clearTimeout(this.testing_timeout)
            })
        },

        save: function () {
            if (this.formValid) {
                this.$emit('callback', {
                    webhook_url: this.webhook.url,
                    webhook_secret: this.webhook.secret
                })
            }
            this.show_modal = false
        },
        validateUrl: function (value) {
            return /^(?:(?:(?:https):)?\/\/)(?:\S+(?::\S*)?@)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,})))(?::\d{2,5})?(?:[/?#]\S*)?$/i.test(value);
        },
        validateNotempty: function (value) {
            var re = /^\s*\S+.*/
            return re.test(value)
        }
    },

    computed: {
        formValid() {
            return this.validateNotempty(this.webhook.url)
                && this.validateUrl(this.webhook.url)
                && !this.testing
                && !this.test_failed
                && (this.last_test_succeeded || (!this.is_webhook_creation && this.webhook.url == this.initialUrl))
        },
        validUrl() {
            return this.validateUrl(this.webhook.url)
        },
        testButtonTooltip() {
            if (this.testing) {
                return 'Waiting…'
            }
            if (this.test_failed) {
                return this.test_failed
            }
            if (this.test_succeeded) {
                return this.test_succeeded
            }
            return this.validUrl ? 'Test Endpoint' : null
        }
    }

}

</script>
